import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import PageLoader from './PageLoader';
import { useAuth } from '../userManagement/AuthContext';
import { useAuthorization } from './Authorization';

/**
 * ProtectedRoute component restricts access to authenticated users and optionally by permissions, roles, or tags
 * 
 * @param {object} props Component props
 * @param {React.ReactNode} props.children Child components to render if access is granted
 * @param {string} [props.permission] Single permission required to access the route
 * @param {string[]} [props.permissions] List of permissions required to access the route
 * @param {string[]} [props.roles] List of roles required to access the route
 * @param {string[]} [props.tags] List of tags required to access the route
 * @param {boolean} [props.requireAll=false] If true, all permissions in the list are required (AND logic)
 * @param {string} [props.redirectTo='/login'] Path to redirect to if access is denied
 * @returns {React.ReactNode} Protected route component
 */
const ProtectedRoute = ({
  children,
  permission,
  permissions,
  roles,
  tags,
  requireAll = false,
  redirectTo = '/login',
  loginPath = '/login'   // where to send unauthenticated users (customer portal uses its own)
}) => {
  const { isAuthenticated, user, authLoading } = useAuth();
  const { hasPermission, hasAllPermissions, hasAnyPermission, isLoading: permissionsLoading } = useAuthorization();
  const location = useLocation();

  // If still loading authentication or permissions, show loading indicator
  if (authLoading || permissionsLoading) {
    return <PageLoader />;
  }

  // If user is not authenticated, redirect to the login path.
  // Save the attempted location so login can redirect back after success
  if (!isAuthenticated) {
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Bug reporting is available to every signed-in account. This exact route
  // is the only ERP exception; customer sessions stay confined elsewhere.
  if (localStorage.getItem('loginChannel') === 'customer' && !location.pathname.startsWith('/customer') && location.pathname !== '/admin/bug-reports') {
    return <Navigate to={localStorage.getItem('pmiCustomerSession') === 'true' ? '/customer/pmi' : '/customer/booking'} replace />;
  }

  // PMI Platform is still an internal work area. Keep it unavailable to all
  // non-admin users, even when they have permissions for individual widgets.
  const isPmiPath =
    location.pathname.startsWith('/admin/pmi-platform') ||
    location.pathname === '/admin/pmi' ||
    location.pathname.startsWith('/admin/pmi/') ||
    location.pathname.startsWith('/admin/pmi-') ||
    location.pathname === '/admin/hospitality' ||
    location.pathname.startsWith('/admin/hospitality/') ||
    location.pathname === '/admin/pmi-restaurant' ||
    location.pathname.startsWith('/admin/pmi-restaurant/');

  if (isPmiPath && location.pathname !== '/admin/pmi-platform/tasks' && user?.is_admin !== true) {
    return <Navigate to={redirectTo} replace />;
  }

  // Admin users bypass permission checks
  if (user?.is_admin) {
    return children;
  }

  // Check if user has required role or tag
  if (roles && roles.length > 0) {
    const userRoles = user?.roles || [];
    const userTags = user?.tags || [];
    const hasRole = roles.some(role => userRoles.includes(role));
    const hasTag = roles.some(role => userTags.includes(role));
    
    if (hasRole || hasTag) {
      return children;
    }
  }

  // Check if user has required tag
  if (tags && tags.length > 0) {
    const userTags = user?.tags || [];
    const hasTag = tags.some(tag => userTags.includes(tag));
    
    if (hasTag) {
      return children;
    }
  }

  // Check specific permissions if they're required
  if (permission || permissions) {
    let hasAccess = false;

    if (permission) {
      // Single permission check
      hasAccess = hasPermission(permission);
    } else if (permissions) {
      // Multiple permissions check
      hasAccess = requireAll
        ? hasAllPermissions(permissions)
        : hasAnyPermission(permissions);
    }

    // Fallback: check userPermissions directly from AuthContext
    // This avoids the race condition where Authorization useEffect hasn't processed them yet
    if (!hasAccess && user?.permissions && Array.isArray(user.permissions)) {
      const permList = permission ? [permission] : (permissions || []);
      hasAccess = permList.some(p => user.permissions.includes(p));
    }

    if (!hasAccess) {
      // Redirect to unauthorized page if user doesn't have required permissions
      return <Navigate to={redirectTo} replace />;
    }
  }

  // User is authenticated and has required permissions (if any), render children
  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  permission: PropTypes.string,
  permissions: PropTypes.arrayOf(PropTypes.string),
  roles: PropTypes.arrayOf(PropTypes.string),
  tags: PropTypes.arrayOf(PropTypes.string),
  requireAll: PropTypes.bool,
  redirectTo: PropTypes.string,
  loginPath: PropTypes.string,
};

export default ProtectedRoute;
